import React, { useMemo } from 'react';
import { useLocale } from 'locale';
import { useScreenBreakpoint } from 'consts/breakpoints';
import * as Styled from './styled';
import { getBuildingClassKey, BuildingClass, InvestorsData, User, DealType, RegisterType } from 'utils/entities';
import { AgentTags } from 'components/cards/listing/UniversalCard/AgentTags';
import OfferDetails from './offer-details';
import { createOfferTypesByMarketPlace, createOfferProps } from './offer-details/helper';
import useScrollToPriceEstimation from 'hooks/useScrollToPriceEstimation';
import { formatPrimaryAndSecondaryAddress } from 'utils/addressFormatter';
import InvestorInfo from './investor-info';

interface MainInfoProps {
  id: string;
  currentPrice: number;
  primaryAddress: string;
  secondaryAddress: string;
  neighbourhoodDocId: string;
  pricePerSizeUnit?: number;
  dealType: string;
  floor?: string | number;
  floors?: string | number;
  beds?: number;
  area?: number;
  buildingClass: BuildingClass;
  priceDropped?: number;
  isCommercial?: boolean;
  user?: User;
  investorsData?: InvestorsData;
  onOpenAuthModal?: () => void;
  openYieldTagModal?: (id: string) => void;
  onInvestorsLinkClick?: () => void;
  sendPriceEstimationClickEvent?: () => void;
}

export interface AgentProps {
  isExclusive?: boolean;
  isAgent?: boolean;
}

export type WrapperMainInfoProps = MainInfoProps & AgentProps;

export const MainInfo: React.FC<WrapperMainInfoProps> = React.memo(({
  currentPrice,
  primaryAddress,
  secondaryAddress,
  pricePerSizeUnit,
  dealType,
  beds,
  floor,
  area,
  isAgent,
  isExclusive,
  priceDropped,
  floors,
  buildingClass,
  isCommercial,
  user,
  investorsData,
  openYieldTagModal,
  sendPriceEstimationClickEvent,
  id,
  neighbourhoodDocId,
}) => {
  const { t, formatMoney, formatArea } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint <= 2;

  const offers = useMemo(() => createOfferTypesByMarketPlace(isCommercial), [ isCommercial ]);

  const scrollToPriceEstimation = useScrollToPriceEstimation(sendPriceEstimationClickEvent);

  const showEstimatedPrice = dealType === DealType.Buy && !isCommercial;
  const isVisitor = !user || user.registrationType === RegisterType.Visitor;

  return (
    <Styled.Root>
      <Styled.PriceInfoWrapper>
        <Styled.PriceInfo>
          <Styled.CurrentPriceText data-auto="current-price">{formatMoney(currentPrice)}</Styled.CurrentPriceText>
          {isMobile ? (
            <div>
              <Styled.PrimaryAddressText data-auto="primary_address_text">
                {formatPrimaryAndSecondaryAddress(primaryAddress, secondaryAddress)}
              </Styled.PrimaryAddressText>
              <Styled.SecondaryAddress data-auto="secondary_address">
                {t('unitPage.dealTypeLabel', { buildingClass: getBuildingClassKey(buildingClass), dealType, isCommercial })}
                {isAgent ? <AgentTags isExclusive={isExclusive} /> : null}
              </Styled.SecondaryAddress>
            </div>
          ) : null}
          <Styled.OfferDetailsContainer>
            {offers.map((offerType, i, arr) => {
              const offerProps = createOfferProps(offerType, { beds, t, area, floor, floors, isCommercial, pricePerSizeUnit, formatMoney, formatArea, dealType, withBackDivider: i !== (arr.length - 1) });
              return offerProps ? <OfferDetails key={offerProps.locator} {...offerProps} /> : null;
            })}
          </Styled.OfferDetailsContainer>
          {isMobile ? (
            <InvestorInfo
              listingId={id}
              neighbourhoodDocId={neighbourhoodDocId}
              investorsData={investorsData}
              priceDropped={priceDropped}
              showEstimatedPrice={showEstimatedPrice}
              scrollToPriceEstimation={scrollToPriceEstimation}
              openYieldTagModal={openYieldTagModal}
              isVisitor={isVisitor}
            />
          ) : null}
        </Styled.PriceInfo>
        {!isMobile ? (
          <div>
            <Styled.PrimaryAddressTextDesktop weight="medium" data-auto="primary_address_text">{formatPrimaryAndSecondaryAddress(primaryAddress, secondaryAddress)}</Styled.PrimaryAddressTextDesktop>
            <Styled.SecondaryAddress data-auto="secondary_address">
              {t('unitPage.dealTypeLabel', { buildingClass: getBuildingClassKey(buildingClass), dealType, isCommercial })}
              {isAgent ? <AgentTags isExclusive={isExclusive} /> : null}
            </Styled.SecondaryAddress>
          </div>
        ) : null}
      </Styled.PriceInfoWrapper>
      {!isMobile ? (
        <InvestorInfo
          listingId={id}
          neighbourhoodDocId={neighbourhoodDocId}
          investorsData={investorsData}
          priceDropped={priceDropped}
          showEstimatedPrice={showEstimatedPrice}
          scrollToPriceEstimation={scrollToPriceEstimation}
          openYieldTagModal={openYieldTagModal}
          lastRowMarginTop={4}
          isVisitor={isVisitor}
        />
      ) : null}
    </Styled.Root>
  );
});
